import { useState } from 'react';
import { DraftChips } from '../components/primitives';
import { useComposerChips } from '../components/agent/useComposerChips';
import { SkillCard } from '../components/agent/SkillCard';
import { buildSkillChip } from '../components/agent/buildSkillChip';
import { compileSkillSpec } from '../lib/composer/compileRegistry';
import type { InstalledSkill } from '../lib/models/skills';
import type { SkillParameter } from '../lib/models/skillParameters';

/**
 * /#/__test/skill-card — deterministic Playwright + manual harness for the
 * Skills 1.5 curated skill card.
 *
 * Mounts the REAL `<SkillCard>` + `buildSkillChip` + `useComposerChips` +
 * `<DraftChips>` + the real `compileSkillSpec`, fed a SYNTHETIC skill set so the
 * Gherkin scenarios run without Supabase. It replicates AgentChat's open-card
 * decision (parameterised → card; no-param → instant-insert) so the "drops
 * straight in" path is covered too.
 *
 * Testids: skill-open-deep-research, skill-open-format-changelog, skill-card,
 * skill-card-insert, skill-field-<name>, draftchips, chip-count, chip-spec,
 * chip-directive.
 */
function mkSkill(over: Partial<InstalledSkill> & { id: string; name: string }): InstalledSkill {
  return {
    orgId: 'org-test-1',
    source: 'github',
    repoUrl: `https://github.com/acme/${over.name}`,
    pinnedVersion: 'v2.1',
    enabled: true,
    approvalStatus: 'approved',
    approvedBy: null,
    approvedAt: null,
    requestedBy: null,
    requestedAt: null,
    manifest: null,
    parameters: [],
    storagePath: 'org/org-test-1/skill/stored/v2.1/',
    contentHash: 'test-hash',
    fileManifest: null,
    stored: true,
    ...over
  };
}

const DEEP_PARAMS: SkillParameter[] = [
  { name: 'question', type: 'string', required: true },
  { name: 'depth', type: 'enum', required: false, options: ['quick', 'standard', 'deep'], default: 'standard' },
  { name: 'sources', type: 'integer', required: false, default: 5 }
];

const DEEP_RESEARCH = mkSkill({
  id: 'sk_deepresearch',
  name: 'deep-research',
  parameters: DEEP_PARAMS,
  manifest: { parameters: DEEP_PARAMS }
});
const FORMAT_CHANGELOG = mkSkill({ id: 'sk_fmt', name: 'format-changelog', parameters: [] });

export function TestSkillCard() {
  const chips = useComposerChips();
  const [card, setCard] = useState<InstalledSkill | null>(null);

  // Mirrors AgentChat.onLauncherSelect: parameterised → open card; else insert.
  const selectSkill = (skill: InstalledSkill) => {
    if (skill.parameters.length > 0) {
      setCard(skill);
      return;
    }
    chips.addChip(buildSkillChip(skill));
  };

  const insert = (params: Record<string, string | number | boolean>) => {
    if (!card) return;
    chips.addChip(buildSkillChip(card, params));
    setCard(null);
  };

  const last = chips.state.chips[chips.state.chips.length - 1] ?? null;
  const directive = last ? compileSkillSpec(last.spec) : null;

  return (
    <div style={{ padding: 'var(--space-8)', maxWidth: '40rem' }}>
      <h1>Skill card harness</h1>
      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBlockEnd: 'var(--space-6)' }}>
        <button
          type="button"
          className="pn-btn"
          data-testid="skill-open-deep-research"
          onClick={() => selectSkill(DEEP_RESEARCH)}
        >
          Open deep-research
        </button>
        <button
          type="button"
          className="pn-btn"
          data-testid="skill-open-format-changelog"
          onClick={() => selectSkill(FORMAT_CHANGELOG)}
        >
          Open format-changelog
        </button>
      </div>

      {card ? <SkillCard skill={card} onInsert={insert} onCancel={() => setCard(null)} /> : null}

      <div style={{ marginBlockStart: 'var(--space-8)' }}>
        <DraftChips
          chips={chips.state.chips.map((c) => ({
            id: c.id,
            kind: c.kind,
            label: c.label,
            value: c.value
          }))}
          onRemove={(id) => chips.removeChip(id)}
        />
      </div>

      <pre data-testid="chip-count">{chips.state.chips.length}</pre>
      <pre data-testid="chip-spec">{last ? JSON.stringify(last.spec) : ''}</pre>
      <pre data-testid="chip-directive">{directive ? JSON.stringify(directive) : ''}</pre>
    </div>
  );
}
